import { observer } from 'mobx-react';
import { FC, ReactElement, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { ProfileBranch } from '../../components/ProfileBranch/ProfileBranch';
import { Icons } from '~/assets/fonts/Icons/icons';
import theme from '~/constants/styles';
import { branchStore } from '~/mobx/branchStore';
import { localeStore } from '~/mobx/localesStore';
import { IBranch } from '~/types/branches';
import { IProfileBranch } from '~/types/profiles';
import { emptyArray } from '~/utils/helpers';
import Icon from '~/view/components/Controls/Icon/Icon';
import { IConfirmationMessageProps } from '~/utils/modalConfirmations';
import { TProfile } from '~/view/containers/ProfilesList';
import './style.scss';

interface IProps {
  selectedProfile?: TProfile;
  onDeleteBranch?: (profileId: IProfileBranch['id']) => void;
  deleteConfirmationOptions?: IConfirmationMessageProps;
  lockDeleteInDefaultProfile?: boolean;
  availableDragAndDrop?: boolean;
  loading?: boolean;
  isOverlayBranch?: boolean;
  className?: string;
  notEditable?: boolean;
}

export const ProfileBranchesList: FC<IProps> = observer(({ selectedProfile, lockDeleteInDefaultProfile, ...props }: IProps): ReactElement | null => {
  const isNoFound = useMemo((): boolean => Boolean(selectedProfile && !selectedProfile.branches?.length), [selectedProfile?.branches]);
  const lockDelete = useMemo((): boolean => {
    return Boolean((lockDeleteInDefaultProfile && selectedProfile?.is_default) || typeof props.onDeleteBranch !== 'function');
  }, [lockDeleteInDefaultProfile, selectedProfile?.is_default]);

  if (isNoFound) {
    return (
      <div className="profile-branches-list__no-found">
        <Icon name={Icons.BranchesTree} size={55} color={theme.colors.gray} className="my-6" />
        <p className="profile-branches-list__no-found-text">{localeStore.t('no_branches_in_this_profile')}</p>
      </div>
    );
  }

  if (props.loading) {
    return (
      <>
        {emptyArray(4).map((_, index) => (
          <Skeleton key={index} height={26} width={249} className="ml-6 my-2" />
        ))}
      </>
    );
  }

  return (
    <>
      {selectedProfile?.branches?.map((branch) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <ProfileBranch key={branch.id} branch={branch} lockDelete={lockDelete} {...props} />
      ))}
    </>
  );
});
